import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchHistories } from "../../../api/histories";
import { fetchUsers } from "../../../api/users";
import { fetchProjects } from "../../../api/project";
import { buildProjectsIndexParams } from "../../../utils/projectsIndexParams";
import { fetchCustomers } from "../../../api/customer";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import { dateToString } from "../../../components/Utils";
import { Link } from "react-router-dom";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ClockIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import ArrowCircleRightSharp from "@mui/icons-material/ArrowCircleRightSharp";

// History action types and their icons
const historyActionTypes = {
  "create": { icon: PlusIcon, bgColorClass: "bg-green-400", label: "Created" },
  "update": { icon: CheckIcon, bgColorClass: "bg-blue-400", label: "Updated" },
  "destroy": { icon: TrashIcon, bgColorClass: "bg-red-400", label: "Deleted" },
  "new_agreement": {
    icon: ClockIcon,
    bgColorClass: "bg-pink-400",
    label: "New Agreement",
  },
  "sync_error": {
    icon: ExclamationTriangleIcon,
    bgColorClass: "bg-red-400",
    label: "Sync Error",
  },
  "sync_api": {
    icon: ArrowCircleRightSharp,
    bgColorClass: "bg-blue-400",
    label: "API Sync",
  },
  "new_message": {
    icon: CheckIcon,
    bgColorClass: "bg-green-400",
    label: "New Message",
  },
  "answer": { icon: CheckIcon, bgColorClass: "bg-blue-400", label: "Answer" },
  "update_product": {
    icon: CheckIcon,
    bgColorClass: "bg-blue-400",
    label: "Product Updated",
  },
  "sync_success": {
    icon: CheckIcon,
    bgColorClass: "bg-green-400",
    label: "Sync Success",
  },
  "tripletex_sync_attempt": {
    icon: ArrowCircleRightSharp,
    bgColorClass: "bg-sky-400",
    label: "Tripletex sync attempt",
  },
  "tripletex_sync": {
    icon: CheckIcon,
    bgColorClass: "bg-emerald-500",
    label: "Tripletex synced",
  },
  "tripletex_sync_skipped": {
    icon: ExclamationTriangleIcon,
    bgColorClass: "bg-amber-500",
    label: "Tripletex sync skipped",
  },
  "processed": {
    icon: CheckIcon,
    bgColorClass: "bg-blue-400",
    label: "Processed",
  },
  "request_agreed_overtime": {
    icon: ExclamationTriangleIcon,
    bgColorClass: "bg-amber-500",
    label: "Requested agreed overtime",
  },
  "confirm_agreed_overtime": {
    icon: CheckIcon,
    bgColorClass: "bg-emerald-500",
    label: "Confirmed agreed overtime",
  },
  "reject_agreed_overtime": {
    icon: XCircleIcon,
    bgColorClass: "bg-red-400",
    label: "Rejected agreed overtime",
  },
  "not_approved_notification": {
    icon: CheckIcon,
    bgColorClass: "bg-rose-400",
    label: "Sent email: not approved",
  },
  "approved_notification": {
    icon: CheckIcon,
    bgColorClass: "bg-emerald-400",
    label: "Sent email: approved",
  },
};

// Controller labels
const historyControllers = {
  "user": "Users",
  "report": "Reports",
  "order": "Orders",
  "tripletex_api": "Tripletex API",
  "customer_system": "Customer System",
  "text_message": "Text Messages",
  "product": "Products",
  "customer": "Customers",
  "project": "Projects",
  "case": "Cases",
  "service_agreement": "Service Agreements",
  "inventory": "Inventory",
  "quality": "Quality",
  "driving": "Driving",
  "hours": "Hours",
  "hour": "Hours",
  "absence": "Absences",
  "work_order": "Work Orders",
  "offer": "Offers",
  "template": "Templates",
  "webpage": "Webpages",
  "company": "Company",
  "integration": "Integrations",
};

export const CompanyHistory = () => {
  const { t } = useTranslation();
  const [histories, setHistories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    users: [],
    projects: [],
    customers: [],
    contacts: [],
    departments: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentFilters, setCurrentFilters] = useState({
    query: "",
    modules: [],
    users: [],
    statuses: [],
  });

  // Simple fetch function
  const loadHistories = async () => {
    setIsLoading(true);
    try {
      const filters = [];

      if (currentFilters.modules.length > 0) {
        currentFilters.modules.forEach((module) => {
          filters.push({ name: "controller", value: module });
        });
      }

      if (currentFilters.users.length > 0) {
        currentFilters.users.forEach((user) => {
          filters.push({ name: "user_id", value: user });
        });
      }

      if (currentFilters.statuses.length > 0) {
        currentFilters.statuses.forEach((status) => {
          filters.push({ name: "action", value: status });
        });
      }

      const response = await fetchHistories({
        page: currentPage,
        per_page: 50,
        query: currentFilters.query,
        sort: "desc",
        sortBy: "updated_at",
        view: "all",
        filters,
      });

      if (response?.data?.data) {
        const processedHistories = response.data.data.map((history) => ({
          ...history,
          type: historyActionTypes[history.action] || {
            icon: CheckIcon,
            bgColorClass: "bg-gray-400",
            label: history.action,
          },
        }));
        setHistories(processedHistories);
      } else {
        setHistories([]);
      }
    } catch (error) {
      console.error("Error fetching histories:", error);
      setHistories([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load filter options once on mount
  const loadFilterOptions = async () => {
    try {
      const [usersResponse, projectsResponse, customersResponse] =
        await Promise.all([
          fetchUsers({ per_page: 100 }),
          fetchProjects(
            buildProjectsIndexParams({
              per_page: 100,
              sort: "desc",
              sortBy: "created_at",
            })
          ),
          fetchCustomers({ per_page: 100 }),
        ]);

      setFilterOptions({
        users: usersResponse?.data?.data || [],
        projects: projectsResponse?.data?.data || [],
        customers: customersResponse?.data?.data || [],
        contacts: [],
        departments: [],
      });
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  // Initial load
  useEffect(() => {
    loadFilterOptions();
    loadHistories();
  }, []);

  // Load histories when page or filters change
  useEffect(() => {
    loadHistories();
  }, [currentPage, currentFilters]);

  const handleFiltersChange = (newFilters) => {
    setCurrentFilters(newFilters);
    setCurrentPage(1); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getActionIcon = (action) => {
    const actionType = historyActionTypes[action];
    if (actionType) {
      const IconComponent = actionType.icon;
      return (
        <div className={`p-2 rounded-full ${actionType.bgColorClass}`}>
          <IconComponent className="h-4 w-4 text-white" />
        </div>
      );
    }
    return (
      <div className="p-2 rounded-full bg-gray-400">
        <CheckIcon className="h-4 w-4 text-white" />
      </div>
    );
  };

  const getControllerLabel = (controller) => {
    return historyControllers[controller] || controller;
  };

  const getActionLabel = (action) => {
    const key = `histories.action.${action}`;
    const translated = t(key);
    if (translated !== key) return translated;
    return historyActionTypes[action]?.label || action;
  };

  const outboundCommunicationId = (history) => {
    if (history?.outbound_communication_id) return history.outbound_communication_id;
    const data = history?.data && typeof history.data === "object" ? history.data : null;
    return data?.outbound_communication_id || null;
  };

  const getHistoryDescription = (history) => {
    const controller = getControllerLabel(history.controller);
    const action = getActionLabel(history.action);

    if (
      history.controller === "text_message" &&
      history.action === "new_message"
    ) {
      return `Sent SMS to ${history.param_two || "recipient"}`;
    }

    if (history.controller === "tripletex_api") {
      return `Tripletex API operation: ${action}`;
    }

    if (
      history.action === "not_approved_notification" ||
      history.action === "approved_notification"
    ) {
      return history.action === "approved_notification"
        ? t("histories.outbound.approved")
        : t("histories.outbound.not_approved");
    }

    if (
      history.controller === "hour" &&
      (history.action?.startsWith?.("tripletex_sync") ||
        history.action === "request_agreed_overtime" ||
        history.action === "confirm_agreed_overtime" ||
        history.action === "reject_agreed_overtime")
    ) {
      const key = `histories.action.${history.action}`;
      const translated = t(key);
      const verb = translated !== key ? translated : history.action;
      return `${verb} ${getControllerLabel("hour")}`;
    }

    return `${action} ${controller}`;
  };

  const getHistoryLink = (history) => {
    const outboundId = outboundCommunicationId(history);
    if (outboundId) {
      return `/dashboard/communications/${outboundId}`;
    }

    switch (history.controller) {
      case "order":
        return history.order?.id
          ? `/dashboard/orders/${history.order.id}`
          : null;
      case "report":
        return history.report?.id
          ? `/dashboard/service/reports/${history.report.id}`
          : null;
      case "customer":
        return history.customer?.id
          ? `/dashboard/customers/${history.customer.id}`
          : null;
      case "project":
        return history.project?.id
          ? `/dashboard/projects/${history.project.id}`
          : null;
      case "case":
        return history.case?.id ? `/dashboard/cases/${history.case.id}` : null;
      case "product":
        return history.product?.id
          ? `/dashboard/products/${history.product.id}`
          : null;
      case "user":
        return history.user?.id ? `/dashboard/users/${history.user.id}` : null;
      case "hour":
        return history.param ? `/dashboard/hours/${history.param}` : null;
      default:
        return null;
    }
  };

  const getHistoryTitle = (history) => {
    const link = getHistoryLink(history);
    const description = getHistoryDescription(history);

    if (link) {
      return (
        <Link to={link} className="text-blue-600 hover:text-blue-800 underline">
          {description}
        </Link>
      );
    }

    return <span>{description}</span>;
  };

  // Generate options for ModuleFilter
  const moduleOptions = Object.entries(historyControllers).map(
    ([value, label]) => ({
      value,
      label,
    })
  );

  const userOptions = filterOptions.users.map((user) => ({
    value: user.id.toString(),
    label: user.name || user.email || `User ${user.id}`,
  }));

  const projectOptions = filterOptions.projects.map((project) => ({
    value: project.id.toString(),
    label: project.title || project.name || `Project ${project.id}`,
  }));

  const customerOptions = filterOptions.customers.map((customer) => ({
    value: customer.id.toString(),
    label: customer.name || customer.company_name || `Customer ${customer.id}`,
  }));

  const contactOptions = filterOptions.contacts.map((contact) => ({
    value: contact.id.toString(),
    label: contact.name || contact.email || `Contact ${contact.id}`,
  }));

  const departmentOptions = filterOptions.departments.map((department) => ({
    value: department.id.toString(),
    label: department.name || `Department ${department.id}`,
  }));

  if (isLoading && histories.length === 0) {
    return <ResourceLoading />;
  }

  // Calculate status statistics
  const statusStats = histories.reduce((acc, history) => {
    const action = history.action?.toLowerCase();
    if (action) {
      acc[action] = (acc[action] || 0) + 1;
    }
    return acc;
  }, {});

  const totalEntries = histories.length;

  return (
    <>
      <DashboardHeader
        subtitle={t("Track all activities and events for your company")}
        title={t("Company History")}
        meta={[
          {
            label: `${totalEntries} entries`,
            icon: ClockIcon,
            textColor: "text-gray-500",
            iconColor: "text-gray-400",
          },
          ...Object.entries(statusStats)
            .slice(0, 3)
            .map(([action, count]) => ({
              label: `${count} ${historyActionTypes[action]?.label || action}`,
              icon: historyActionTypes[action]?.icon || CheckIcon,
              textColor: "text-gray-500",
              iconColor:
                historyActionTypes[action]?.bgColorClass?.replace(
                  "bg-",
                  "text-"
                ) || "text-gray-400",
            })),
        ]}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            {/* Filters */}
            <div className="bg-white shadow rounded-lg p-6">
              <ModuleFilter
                onFiltersChange={handleFiltersChange}
                initialFilters={currentFilters}
                searchPlaceholder="Search history..."
                filterTitle="Filter History"
                moduleOptions={moduleOptions}
                userOptions={userOptions}
                projectOptions={projectOptions}
                customerOptions={customerOptions}
                contactOptions={contactOptions}
                departmentOptions={departmentOptions}
                acceptedStatuses={Object.keys(historyActionTypes)}
              />
            </div>

            {/* History List */}
            <div className="bg-white shadow rounded-lg">
              {histories.length === 0 ? (
                <ResourceEmpty
                  title={t("No History Found")}
                  description={t(
                    "No history entries match your current filters"
                  )}
                />
              ) : (
                <div className="divide-y divide-gray-200">
                  {histories.map((history, index) => {
                    const itemStatus = history.action?.toLowerCase();

                    return (
                      <div
                        key={history.id || index}
                        className="p-6 hover:bg-gray-50"
                      >
                        <div className="flex items-start space-x-4">
                          {/* Action Icon */}
                          <div className="flex-shrink-0">
                            {getActionIcon(history.action)}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-medium text-gray-900">
                                  {getHistoryTitle(history)}
                                </p>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {getControllerLabel(history.controller)}
                                </span>
                                {/* Status Badge */}
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    itemStatus === "sync_error"
                                      ? "bg-red-100 text-red-800"
                                      : itemStatus === "create"
                                      ? "bg-green-100 text-green-800"
                                      : itemStatus === "update"
                                      ? "bg-blue-100 text-blue-800"
                                      : itemStatus === "destroy"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {getActionLabel(history.action)}
                                </span>
                              </div>
                              <div className="text-sm text-gray-500">
                                {dateToString(history.updated_at)}
                              </div>
                            </div>

                            {/* User Info */}
                            {(history.created_by || history.user) && (
                              <div className="mt-2 flex items-center space-x-2">
                                <span className="text-sm text-gray-500">
                                  {t("By")}:
                                </span>
                                <span className="text-sm font-medium text-gray-900">
                                  {(history.created_by || history.user).name ||
                                    (history.created_by || history.user).email ||
                                    "Unknown User"}
                                </span>
                              </div>
                            )}

                            {/* Additional Details */}
                            {history.param_one && (
                              <div className="mt-2 text-sm text-gray-600">
                                {history.param_one}
                              </div>
                            )}

                            {/* Error Details for Failed Events */}
                            {history.action === "sync_error" &&
                              (history.param_two || history.data) && (
                                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                                  <div className="flex items-center space-x-2">
                                    <ExclamationTriangleIcon className="h-4 w-4 text-red-400" />
                                    <span className="text-sm font-medium text-red-800">
                                      {t("Sync Error Details")}
                                    </span>
                                  </div>
                                  <div className="mt-2 text-sm text-red-700">
                                    {history.param_two || (typeof history.data === "string"
                                      ? history.data
                                      : JSON.stringify(history.data, null, 2))}
                                  </div>
                                </div>
                              )}

                            {history.changes &&
                              typeof history.changes === "object" &&
                              Object.keys(history.changes).length > 0 && (
                                <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                                  <div className="mb-2 text-sm font-medium text-slate-800">
                                    {t("histories.sync_changes_heading")}
                                  </div>
                                  <pre className="whitespace-pre-wrap text-sm text-slate-700">
                                    {JSON.stringify(history.changes, null, 2)}
                                  </pre>
                                </div>
                              )}

                            {/* Data Changes */}
                            {history.data &&
                              history.action !== "sync_error" && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                  <div className="text-sm font-medium text-blue-800 mb-2">
                                    {t("Changes")}:
                                  </div>
                                  <div className="text-sm text-blue-700">
                                    <pre className="whitespace-pre-wrap">
                                      {typeof history.data === "string"
                                        ? history.data
                                        : JSON.stringify(history.data, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            {histories.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    {t("Showing")} {histories.length} {t("of")}{" "}
                    {histories.length} {t("entries")}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("Previous")}
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={histories.length < 50}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("Next")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DashboardHeader>
    </>
  );
};
